import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { TrendingUp, TrendingDown, Zap, Wind, Sun, AlertTriangle, Activity, Globe, Info, Sparkles, Lightbulb, MoreVertical, Share2, Copy, Trash2, X, ArrowUpDown, Bookmark, MapPin, LayoutDashboard, Plus, ChevronDown, Check } from 'lucide-react';
import { generateMarketData, generateModelAccuracy, fetchEntsoeMarketData } from '../../services/api';
import { MarketZone } from '../../types/data';
import { loadMarketDataFromCSV, getLatestDataByRegion, getAvailableRegions } from '../../services/csvParser';
import { ChartsPanel } from '../ChartsPanel';
import { VariableType } from '../../hooks/useDashboardState';
interface MetricCardProps {
  title: string;
  value: string;
  unit?: string;
  change?: number;
  icon: any;
  trend?: 'up' | 'down' | 'stable';
  region: string;
  timestamp?: Date;
  isRealData?: boolean;
}
function MetricCard({
  title,
  value,
  unit,
  change,
  icon: Icon,
  trend,
  region,
  timestamp,
  isRealData = false
}: MetricCardProps) {
  const getIconColor = () => {
    if (title.includes('Price')) return 'text-blue-600';
    if (title.includes('Load')) return 'text-purple-600';
    if (title.includes('Wind')) return 'text-cyan-600';
    if (title.includes('Solar')) return 'text-orange-600';
    return 'text-slate-600';
  };
  return <motion.div key={`${title}-${region}`} className="bg-white/60 backdrop-blur-xl border border-slate-50 rounded-2xl p-6 pt-8 shadow-sm shadow-slate-200/50 relative overflow-visible group" data-id="element-2623">
      <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-pink-50/30 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl" data-id="element-2624" />

      <div className="absolute top-3 right-3 flex items-center gap-2.5" data-id="element-2625">
        <div className="flex items-center px-2.5 py-1 rounded-md bg-slate-50 border border-slate-200" data-id="element-2626">
          <span className="text-[10px] font-semibold text-slate-600 leading-none" data-id="element-2627">
            {region}
          </span>
        </div>
        {isRealData && <div className="flex items-center px-2.5 py-1 rounded-md bg-green-50 border border-green-200" data-id="element-2628">
            <span className="text-[10px] font-semibold text-green-700 leading-none" data-id="element-2629">
              LIVE
            </span>
          </div>}
      </div>

      <div className="relative" data-id="element-2630">
        <div className="flex items-center gap-2 mb-6" data-id="element-2631">
          <Icon size={20} className={getIconColor()} data-id="element-2632" />
          <h3 className="text-sm font-medium text-slate-500" data-id="element-2633">{title}</h3>
        </div>

        <div className="mb-3" data-id="element-2634">
          <div className="flex items-baseline gap-2" data-id="element-2635">
            <motion.span key={value} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3
          }} className="text-4xl font-bold text-slate-900 tracking-tight" data-id="element-2636">
              {value}
            </motion.span>
            {unit && <span className="text-lg font-medium text-slate-400" data-id="element-2637">{unit}</span>}
          </div>
        </div>

        {change !== undefined && parseFloat(value) > 0 && <div className="flex items-center gap-3" data-id="element-2638">
            <motion.div key={`${change}-${trend}`} initial={{
          opacity: 0,
          x: -10
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.3,
          delay: 0.1
        }} className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold ${trend === 'up' ? 'bg-emerald-50 text-emerald-600' : trend === 'down' ? 'bg-red-50 text-red-600' : 'bg-slate-50 text-slate-600'}`} data-id="element-2639">
              {trend === 'up' ? <TrendingUp size={12} data-id="element-2640" /> : trend === 'down' ? <TrendingDown size={12} data-id="element-2641" /> : null}
              {trend === 'up' ? '+' : ''}
              {Math.abs(change).toFixed(1)}%
            </motion.div>
            <span className="text-xs text-slate-400" data-id="element-2642">vs last hour</span>
          </div>}
      </div>
    </motion.div>;
}
const REGION_NAMES: Record<string, string> = {
  Germany: 'Germany',
  Spain: 'Spain',
  UK: 'United Kingdom',
  France: 'France'
};
const ZONE_MAP: Record<string, MarketZone> = {
  Germany: 'DE',
  Spain: 'ES',
  UK: 'UK',
  France: 'FR',
  Netherlands: 'NL',
  'United Kingdom': 'UK'
};
function RegionalSummaryCard({
  zone,
  data,
  isActive,
  onSelect
}: {
  zone: MarketZone;
  data: any;
  isActive: boolean;
  onSelect: () => void;
}) {
  const [showTooltip, setShowTooltip] = useState(false);
  const formatTimestamp = (date: Date) => {
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  };
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.2
  }} className={`relative p-4 rounded-lg border transition-colors text-left overflow-visible ${isActive ? 'bg-blue-50/80 border-blue-200 shadow-sm shadow-blue-200/50' : 'bg-slate-50/50 border-slate-200/50 hover:bg-slate-50'}`} data-id="element-2643">
      <button onClick={onSelect} className="w-full text-left" data-id="element-2644">
        <div className="flex items-center gap-2 mb-3" data-id="element-2645">
          <span className={`text-sm font-semibold ${isActive ? 'text-blue-900' : 'text-slate-900'}`} data-id="element-2646">
            {zone}
          </span>

          <div className="relative z-50" onClick={e => e.stopPropagation()} data-id="element-2647">
            <button onMouseEnter={() => setShowTooltip(true)} onMouseLeave={() => setShowTooltip(false)} className="p-1 rounded-md hover:bg-slate-200/50 transition-colors" data-id="element-2648">
              <Info size={14} className="text-slate-400" data-id="element-2649" />
            </button>

            {showTooltip && data.timestamp && <motion.div initial={{
            opacity: 0,
            y: 5
          }} animate={{
            opacity: 1,
            y: 0
          }} className="absolute left-0 top-full mt-2 w-56 bg-slate-900 text-white text-xs rounded-lg p-3 shadow-xl z-[100]" data-id="element-2650">
                <div className="space-y-1.5" data-id="element-2651">
                  <div className="text-slate-400" data-id="element-2652">
                    <span data-id="element-2653">Data Timestamp:</span>
                    <div className="font-mono text-slate-200 mt-0.5 text-[11px]" data-id="element-2654">
                      {formatTimestamp(data.timestamp)}
                    </div>
                  </div>
                  <div className="pt-1.5 mt-1.5 border-t border-slate-700 text-slate-400" data-id="element-2655">
                    Market data based on European electricity patterns
                  </div>
                </div>
                <div className="absolute -top-1 left-4 w-2 h-2 bg-slate-900 transform rotate-45" data-id="element-2656" />
              </motion.div>}
          </div>

          <div className="ml-auto" data-id="element-2657">
            {isActive && <div className="px-2 py-1 rounded bg-blue-100 border border-blue-200 flex items-center justify-center" data-id="element-2658">
                <span className="text-[10px] font-bold text-blue-700 leading-none" data-id="element-2659">
                  ACTIVE
                </span>
              </div>}
          </div>
        </div>

        <div className="space-y-2" data-id="element-2660">
          <div data-id="element-2661">
            <p className="text-xs text-slate-600" data-id="element-2662">Spot Price</p>
            <p className="text-lg font-bold text-slate-900" data-id="element-2663">
              {data.spotPrice.toFixed(2)} €/MWh
            </p>
          </div>
          <div data-id="element-2664">
            <p className="text-xs text-slate-600" data-id="element-2665">Load</p>
            <p className="text-sm font-medium text-slate-700" data-id="element-2666">
              {(data.load / 1000).toFixed(1)} GW
            </p>
          </div>
        </div>
      </button>
    </motion.div>;
}
export function OverviewView({
  activeRegion,
  setActiveRegion,
  onNotificationsChange,
  onModalOpenChange
}: {
  activeRegion: string;
  setActiveRegion: (region: string) => void;
  onNotificationsChange?: (notifications: any[]) => void;
  onModalOpenChange?: (isOpen: boolean) => void;
}) {
  const [marketData, setMarketData] = useState<any>(null);
  const [regionsDataMap, setRegionsDataMap] = useState<Record<string, any>>({});
  const [isRealData, setIsRealData] = useState(false);
  const [showDataLoadedToast, setShowDataLoadedToast] = useState(false);
  const [savedKeyPoints, setSavedKeyPoints] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<'insights' | 'trades'>('insights');
  const [selectedKeyPoint, setSelectedKeyPoint] = useState<any | null>(null);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [selectedForecast, setSelectedForecast] = useState<{
    type: 'wind' | 'solar' | 'temperature' | 'price' | 'map';
    region: string;
    title: string;
  } | null>(null);
  // NEW: Dashboard generation state
  const [showDashboardModal, setShowDashboardModal] = useState(false);
  const [selectedInsights, setSelectedInsights] = useState<Set<string>>(new Set());
  const [selectedDashboard, setSelectedDashboard] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [generatedDashboardName, setGeneratedDashboardName] = useState('');
  const [generationStep, setGenerationStep] = useState('');
  // Available dashboards (these match the dashboards in Dashboard.tsx)
  const availableDashboards = [{
    id: 'nordics-weather',
    name: 'Nordics Weather & Hydro',
    description: 'Wind forecasts, hydro reservoir levels, and precipitation analysis',
    icon: Wind,
    keywords: ['nordics', 'norway', 'sweden', 'finland', 'denmark', 'wind', 'hydro', 'scandinavia', 'nordic', 'oslo', 'stockholm', 'helsinki', 'copenhagen']
  }, {
    id: 'germany-pricing',
    name: 'Germany Pricing Dashboard',
    description: 'Day-ahead market analysis, renewable integration, and grid stability',
    icon: TrendingUp,
    keywords: ['germany', 'german', 'de', 'berlin', 'munich', 'hamburg', 'day-ahead', 'renewable']
  }, {
    id: 'spain-solar',
    name: 'Spain Solar Dashboard',
    description: 'Solar generation forecasts, irradiance patterns, and market impact',
    icon: Sun,
    keywords: ['spain', 'spanish', 'es', 'madrid', 'barcelona', 'solar', 'irradiance', 'photovoltaic']
  }, {
    id: 'uk-trading',
    name: 'UK Trading Dashboard',
    description: 'Trading signals, spread opportunities, and market volatility',
    icon: Activity,
    keywords: ['uk', 'united kingdom', 'britain', 'british', 'london', 'trading', 'spread', 'volatility']
  }, {
    id: 'france-market',
    name: 'France Power Market',
    description: 'Nuclear generation, cross-border flows, and baseload stability',
    icon: Zap,
    keywords: ['france', 'french', 'fr', 'paris', 'nuclear', 'baseload', 'edf']
  }];
  // Mock existing dashboards (in real app, load from localStorage or API)
  const existingDashboards = [{
    id: 'dash-1',
    name: 'European Spreads Q1'
  }, {
    id: 'dash-2',
    name: 'Wind Trading Strategy'
  }, {
    id: 'dash-3',
    name: 'German Market Analysis'
  }];
  // Helper to get variable type for ChartsPanel
  const getVariableType = (type: string): VariableType => {
    switch (type) {
      case 'wind':
        return 'Wind';
      case 'solar':
        return 'Solar';
      case 'temperature':
        return 'Temperature';
      default:
        return 'Wind';
    }
  };
  // Load saved key points from Oracle
  useEffect(() => {
    const loadSavedKeyPoints = () => {
      // Start with empty insights on first load - don't load from localStorage initially
      // Only load when explicitly sent from Oracle
      const sentToOverview = localStorage.getItem('oracle-overview-insights');
      const allPoints: any[] = [];
      // Only add sent-to-overview insights (not the saved keypoints)
      if (sentToOverview) {
        try {
          const parsed = JSON.parse(sentToOverview);
          allPoints.push(...parsed.map((p: any) => ({
            ...p,
            timestamp: new Date(p.timestamp),
            type: p.type || 'general',
            fullContent: p.fullContent || p.content
          })));
        } catch (e) {
          console.error('Failed to load overview insights:', e);
        }
      }
      // Sort by timestamp (newest first) and remove duplicates by id
      const uniquePoints = Array.from(new Map(allPoints.map(item => [item.id, item])).values()).sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
      setSavedKeyPoints(uniquePoints);
    };
    loadSavedKeyPoints();
    // Listen for storage changes
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'oracle-overview-insights') {
        loadSavedKeyPoints();
      }
    };
    // Listen for custom event from Oracle when insights are sent
    const handleOracleInsightsUpdated = () => {
      loadSavedKeyPoints();
    };
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('oracle-insights-updated', handleOracleInsightsUpdated);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('oracle-insights-updated', handleOracleInsightsUpdated);
    };
  }, []);
  // Calculate percentage changes (stable, based on region)
  const getStableChange = (region: string, metric: string): number => {
    // Use region and metric as seed for consistent changes
    const seed = (region.charCodeAt(0) + metric.charCodeAt(0)) % 100;
    const baseChange = seed / 100 * 20 - 10; // Range: -10% to +10%
    return parseFloat(baseChange.toFixed(1));
  };
  useEffect(() => {
    // Generate stable data for all regions immediately
    const generatedData: Record<string, any> = {};
    for (const regionZone of ['DE', 'ES', 'UK', 'FR'] as MarketZone[]) {
      generatedData[regionZone] = generateMarketData(regionZone, 1)[0];
    }
    setRegionsDataMap(generatedData);
    const activeZone = ZONE_MAP[activeRegion];
    setMarketData(generatedData[activeZone]);
    setIsRealData(false); // Always use generated data
    // Generate notifications for the TopBar
    const mockNotifications = [{
      id: 'notif-1',
      region: 'Germany',
      regionCode: 'DE',
      type: 'wind',
      icon: 'wind',
      color: 'cyan',
      title: 'High Wind Generation Alert',
      value: '18.2 GW',
      subtitle: 'Peak wind output'
    }, {
      id: 'notif-2',
      region: 'Spain',
      regionCode: 'ES',
      type: 'price',
      icon: 'zap',
      color: 'amber',
      title: 'Spot Price Spike',
      value: '€142.50/MWh',
      subtitle: '+24% from average'
    }, {
      id: 'notif-3',
      region: 'France',
      regionCode: 'FR',
      type: 'renewable',
      icon: 'leaf',
      color: 'emerald',
      title: 'Renewable Energy Milestone',
      value: '68%',
      subtitle: 'Record renewable share'
    }, {
      id: 'notif-4',
      region: 'UK',
      regionCode: 'UK',
      type: 'load',
      icon: 'zap',
      color: 'blue',
      title: 'Peak Demand Period',
      value: '42.1 GW',
      subtitle: 'Evening peak load'
    }, {
      id: 'notif-5',
      region: 'Germany',
      regionCode: 'DE',
      type: 'price',
      icon: 'zap',
      color: 'emerald',
      title: 'Low Price Opportunity',
      value: '€28.40/MWh',
      subtitle: 'Below average pricing'
    }, {
      id: 'notif-6',
      region: 'Spain',
      regionCode: 'ES',
      type: 'wind',
      icon: 'wind',
      color: 'cyan',
      title: 'Wind Forecast Update',
      value: '12.8 GW',
      subtitle: 'Expected peak tomorrow'
    }, {
      id: 'notif-7',
      region: 'UK',
      regionCode: 'UK',
      type: 'renewable',
      icon: 'leaf',
      color: 'emerald',
      title: 'Solar Generation High',
      value: '8.4 GW',
      subtitle: 'Clear weather conditions'
    }, {
      id: 'notif-8',
      region: 'France',
      regionCode: 'FR',
      type: 'load',
      icon: 'zap',
      color: 'amber',
      title: 'Load Forecast Alert',
      value: '58.2 GW',
      subtitle: 'High demand expected'
    }];
    onNotificationsChange?.(mockNotifications);
  }, []); // Only run once on mount
  useEffect(() => {
    const activeZone = ZONE_MAP[activeRegion];
    if (regionsDataMap[activeZone]) {
      setMarketData(regionsDataMap[activeZone]);
    }
  }, [activeRegion, regionsDataMap]);
  if (!marketData) {
    return <div className="flex items-center justify-center h-96" data-id="element-2667">
        <div className="text-center" data-id="element-2668">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600 mx-auto mb-4" data-id="element-2669" />
          <p className="text-slate-600" data-id="element-2670">Loading market data...</p>
        </div>
      </div>;
  }
  const priceChange = getStableChange(activeRegion, 'price');
  const loadChange = getStableChange(activeRegion, 'load');
  const windChange = getStableChange(activeRegion, 'wind');
  const solarChange = getStableChange(activeRegion, 'solar');
  const getTrend = (change: number): 'up' | 'down' | 'neutral' => {
    if (change > 2) return 'up';
    if (change < -2) return 'down';
    return 'neutral';
  };
  const stripMarkdown = (text: string): string => {
    // Guard against non-string values
    if (!text || typeof text !== 'string') {
      return '';
    }
    return text.replace(/\*\*(.+?)\*\*/g, '$1').replace(/\*(.+?)\*/g, '$1').replace(/^#+\s+/gm, '').replace(/^[\-•→]\s+/gm, '').replace(/\[(.+?)\]\(.+?\)/g, '$1').trim();
  };
  // Updated markdown renderer with clickable chart links
  const renderMarkdown = (content: string) => {
    const lines = content.split('\n');
    const elements: JSX.Element[] = [];
    let currentList: string[] = [];
    let listKey = 0;
    const flushList = () => {
      if (currentList.length > 0) {
        elements.push(<ul key={`list-${listKey++}`} className="space-y-1.5 my-3 ml-1" data-id="element-2671">
            {currentList.map((item, idx) => <li key={idx} className="flex items-start gap-2.5" data-id="element-2672">
                <span className="text-pink-600 mt-1 font-bold text-xs flex-shrink-0" data-id="element-2673">
                  →
                </span>
                <span className="flex-1" data-id="element-2674">
                  {formatInlineMarkdownToReact(item)}
                </span>
              </li>)}
          </ul>);
        currentList = [];
      }
    };
    // Convert inline markdown to React elements with clickable links
    const formatInlineMarkdownToReact = (text: string): React.ReactNode => {
      const parts: React.ReactNode[] = [];
      let lastIndex = 0;
      let key = 0;
      // Process bold text
      const boldRegex = /\*\*(.+?)\*\*/g;
      const italicRegex = /(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g;
      // Forecast patterns with click handlers
      const patterns = [{
        regex: /(wind\s+(?:forecast|generation|speed|output|pattern|power)(?:\s+(?:in|for|across))?\s+(?:Germany|Spain|UK|France|Europe|[\w\s]+)?)/gi,
        color: 'text-cyan-600',
        type: 'wind' as const
      }, {
        regex: /(solar\s+(?:forecast|generation|irradiance|output|power)(?:\s+(?:in|for|across))?\s+(?:Germany|Spain|UK|France|Europe|[\w\s]+)?)/gi,
        color: 'text-orange-600',
        type: 'solar' as const
      }, {
        regex: /(temperature\s+(?:forecast|trend|pattern|drop|rise)(?:\s+(?:in|for|across))?\s+(?:Germany|Spain|UK|France|Europe|[\w\s]+)?)/gi,
        color: 'text-purple-600',
        type: 'temperature' as const
      }, {
        regex: /((?:spot\s+)?price\s+(?:forecast|spike|trend|movement)(?:\s+(?:in|for|across))?\s+(?:Germany|Spain|UK|France|Europe|[\w\s]+)?)/gi,
        color: 'text-blue-600',
        type: 'price' as const
      }];
      // Find all forecast matches
      const allMatches: Array<{
        match: RegExpExecArray;
        color: string;
        type: 'wind' | 'solar' | 'temperature' | 'price';
      }> = [];
      patterns.forEach(pattern => {
        let match;
        const regex = new RegExp(pattern.regex.source, pattern.regex.flags);
        while ((match = regex.exec(text)) !== null) {
          allMatches.push({
            match,
            color: pattern.color,
            type: pattern.type
          });
        }
      });
      // Sort matches by position
      allMatches.sort((a, b) => a.match.index - b.match.index);
      // Build the result with clickable links
      allMatches.forEach(({
        match,
        color,
        type
      }) => {
        if (match.index > lastIndex) {
          // Add text before the match (process bold/italic)
          const beforeText = text.substring(lastIndex, match.index);
          parts.push(processSimpleFormatting(beforeText, key++));
        }
        // Extract region from the matched text
        const regionMatch = match[0].match(/(Germany|Spain|UK|France|Europe)/i);
        const region = regionMatch ? regionMatch[1] : 'Europe';
        // Add clickable forecast link that opens modal
        parts.push(<button key={key++} onClick={e => {
          e.stopPropagation();
          setSelectedForecast({
            type,
            region,
            title: match[0]
          });
        }} className={`${color} font-medium underline decoration-dotted underline-offset-2 hover:decoration-solid cursor-pointer transition-all hover:opacity-80`} data-id="element-2675">
            {match[0]}
          </button>);
        lastIndex = match.index + match[0].length;
      });
      // Add remaining text
      if (lastIndex < text.length) {
        parts.push(processSimpleFormatting(text.substring(lastIndex), key++));
      }
      return parts.length > 0 ? parts : text;
    };
    // Helper to process bold and italic
    const processSimpleFormatting = (text: string, baseKey: number): React.ReactNode => {
      const parts: React.ReactNode[] = [];
      let current = text;
      let key = 0;
      // Process bold
      const boldRegex = /\*\*(.+?)\*\*/g;
      let lastIndex = 0;
      let match;
      while ((match = boldRegex.exec(current)) !== null) {
        if (match.index > lastIndex) {
          parts.push(current.substring(lastIndex, match.index));
        }
        parts.push(<strong key={`${baseKey}-bold-${key++}`} className="font-bold text-slate-900" data-id="element-2676">
            {match[1]}
          </strong>);
        lastIndex = match.index + match[0].length;
      }
      if (lastIndex < current.length) {
        parts.push(current.substring(lastIndex));
      }
      return parts.length > 0 ? parts : text;
    };
    lines.forEach((line, idx) => {
      if (line.startsWith('###')) {
        flushList();
        const text = line.replace(/^###\s*/, '');
        elements.push(<h3 key={`h3-${idx}`} className="text-sm font-bold text-slate-900 mt-3 mb-2" data-id="element-2677">
            {formatInlineMarkdownToReact(text)}
          </h3>);
      } else if (line.startsWith('##')) {
        flushList();
        const text = line.replace(/^##\s*/, '');
        elements.push(<h2 key={`h2-${idx}`} className="text-base font-bold text-slate-900 mt-4 mb-2" data-id="element-2678">
            {formatInlineMarkdownToReact(text)}
          </h2>);
      } else if (line.match(/^[\-•→]\s+/)) {
        const text = line.replace(/^[\-•→]\s+/, '');
        currentList.push(text);
      } else if (line.trim() === '') {
        flushList();
        elements.push(<div key={`space-${idx}`} className="h-2" data-id="element-2679" />);
      } else {
        flushList();
        elements.push(<p key={`p-${idx}`} className="leading-relaxed text-slate-700" data-id="element-2680">
            {formatInlineMarkdownToReact(line)}
          </p>);
      }
    });
    flushList();
    return elements;
  };
  const formatTimeAgo = (date: Date) => {
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };
  const handleDeleteKeyPoint = (id: string, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    const updated = savedKeyPoints.filter(kp => kp.id !== id);
    setSavedKeyPoints(updated);
    localStorage.setItem('oracle-saved-keypoints', JSON.stringify(updated));
  };
  const handleShareKeyPoint = (kp: any) => {
    // Simple share - copy to clipboard
    navigator.clipboard.writeText(stripMarkdown(kp.fullContent));
  };
  // Filter key points by type
  const insightPoints = savedKeyPoints.filter(kp => kp.type === 'general');
  const tradePoints = savedKeyPoints.filter(kp => kp.type === 'trade-idea');
  const displayedPoints = activeTab === 'insights' ? insightPoints : tradePoints;
  // Analyze insights to extract key information
  const analyzeInsights = () => {
    const allContent = savedKeyPoints.map(kp => kp.fullContent).join(' ').toLowerCase();
    // Extract regions
    const regions = new Set<string>();
    const regionPatterns = ['Germany', 'Spain', 'UK', 'France', 'Europe', 'Nordics', 'Norway', 'Sweden', 'Finland', 'Denmark'];
    regionPatterns.forEach(region => {
      if (allContent.includes(region.toLowerCase())) {
        regions.add(region);
      }
    });
    // Extract timeframes
    const timeframes = new Set<string>();
    const timePatterns = ['this week', 'next week', 'next month', '7-day', 'tomorrow', 'today'];
    timePatterns.forEach(time => {
      if (allContent.includes(time.toLowerCase())) {
        timeframes.add(time);
      }
    });
    // Extract variables
    const variables = new Set<string>();
    const varPatterns = ['wind', 'solar', 'price', 'temperature', 'load', 'generation', 'hydro'];
    varPatterns.forEach(v => {
      if (allContent.includes(v.toLowerCase())) {
        variables.add(v);
      }
    });
    // Extract trade directions
    const trades = new Set<string>();
    const tradePatterns = ['long', 'short', 'buy', 'sell', 'spread'];
    tradePatterns.forEach(trade => {
      if (allContent.includes(trade.toLowerCase())) {
        trades.add(trade);
      }
    });
    // Smart dashboard matching based on keywords
    let matchedDashboard: string | null = null;
    let maxScore = 0;
    availableDashboards.forEach(dashboard => {
      let score = 0;
      dashboard.keywords.forEach(keyword => {
        if (allContent.includes(keyword.toLowerCase())) {
          score++;
        }
      });
      if (score > maxScore) {
        maxScore = score;
        matchedDashboard = dashboard.id;
      }
    });
    return {
      regions: Array.from(regions),
      timeframes: Array.from(timeframes),
      variables: Array.from(variables),
      trades: Array.from(trades),
      insightCount: insightPoints.length,
      tradeCount: tradePoints.length,
      matchedDashboard,
      matchScore: maxScore
    };
  };
  const handleGenerateDashboard = async () => {
    if (!selectedDashboard) {
      return;
    }
    setIsGenerating(true);
    // Simulate AI processing with realistic steps
    const steps = ['Analyzing insights...', 'Detecting patterns...', 'Matching dashboard template...', 'Loading market data...', 'Preparing visualizations...'];
    for (let i = 0; i < steps.length; i++) {
      setGenerationStep(steps[i]);
      await new Promise(resolve => setTimeout(resolve, 400));
    }
    const dashboard = availableDashboards.find(d => d.id === selectedDashboard);
    if (!dashboard) return;
    setGeneratedDashboardName(dashboard.name);
    setIsGenerating(false);
    handleCloseDashboardModal(); // Use handler instead of direct state setter
    setShowSuccessToast(true);
    // Add dashboard to visible list
    window.dispatchEvent(new CustomEvent('add-dashboard-to-overview', {
      detail: {
        dashboardId: selectedDashboard
      }
    }));
    // Navigate to the selected dashboard after a brief delay
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('navigate-to-view', {
        detail: {
          view: selectedDashboard
        }
      }));
    }, 800);
    // Hide toast after navigation
    setTimeout(() => {
      setShowSuccessToast(false);
      setSelectedInsights(new Set());
      setSelectedDashboard('');
      setGenerationStep('');
    }, 2500);
  };
  const toggleInsightSelection = (id: string) => {
    setSelectedInsights(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };
  // Add handler to clear all insights
  const handleClearInsights = () => {
    setSavedKeyPoints([]);
    localStorage.removeItem('oracle-overview-insights');
  };
  // Add modal handler functions (like DashboardsView pattern)
  const handleOpenKeyPointModal = (keyPoint: any) => {
    setSelectedKeyPoint(keyPoint);
    if (onModalOpenChange) {
      onModalOpenChange(true);
    }
  };
  const handleCloseKeyPointModal = () => {
    setSelectedKeyPoint(null);
    if (onModalOpenChange) {
      onModalOpenChange(false);
    }
  };
  const handleOpenForecastModal = (forecast: any) => {
    setSelectedForecast(forecast);
    if (onModalOpenChange) {
      onModalOpenChange(true);
    }
  };
  const handleCloseForecastModal = () => {
    setSelectedForecast(null);
    if (onModalOpenChange) {
      onModalOpenChange(false);
    }
  };
  const handleOpenDashboardModal = () => {
    setShowDashboardModal(true);
    if (onModalOpenChange) {
      onModalOpenChange(true);
    }
  };
  const handleCloseDashboardModal = () => {
    setShowDashboardModal(false);
    if (onModalOpenChange) {
      onModalOpenChange(false);
    }
  };
  return <div className="space-y-6" data-id="element-2681">
      {/* Success Toast */}
      <AnimatePresence data-id="element-2682">
        {showSuccessToast && <motion.div initial={{
        opacity: 0,
        y: -20,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: -20,
        scale: 0.95
      }} className="fixed top-20 right-6 z-[9999] bg-gradient-to-r from-emerald-500 to-green-500 text-white px-5 py-4 rounded-xl shadow-2xl shadow-emerald-500/30 flex items-center gap-3 border border-emerald-400/20 max-w-md" data-id="element-2683">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0" data-id="element-2684">
              <Check size={20} data-id="element-2685" />
            </div>
            <div className="flex-1" data-id="element-2686">
              <p className="font-semibold text-sm" data-id="element-2687">Dashboard Loaded!</p>
              <p className="text-xs text-emerald-50 mt-0.5" data-id="element-2688">
                Opening "{generatedDashboardName}" with {selectedInsights.size}{' '}
                insights...
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Toast Notification for Real Data Loaded */}
      <AnimatePresence data-id="element-2689">
        {showDataLoadedToast && <motion.div initial={{
        opacity: 0,
        y: -20,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: -20,
        scale: 0.95
      }} className="fixed top-20 right-6 z-[9999] bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-3 rounded-xl shadow-2xl shadow-green-500/30 flex items-center gap-3 border border-green-400/20" data-id="element-2690">
            <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center" data-id="element-2691">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-2692">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-2693" />
              </svg>
            </div>
            <div data-id="element-2694">
              <p className="font-semibold text-sm" data-id="element-2695">Real Market Data Loaded</p>
              <p className="text-xs text-green-50" data-id="element-2696">
                Now showing live ENTSO-E data
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>

      <div data-id="element-2697">
        <h2 className="text-2xl font-bold text-slate-900" data-id="element-2698">Market Overview</h2>
        <p className="text-sm text-slate-500 mt-1" data-id="element-2699">
          Real-time electricity market data across Europe
        </p>
      </div>

      <div key={activeRegion} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 relative" style={{
      isolation: 'isolate'
    }} data-id="element-2700">
        <MetricCard title="Spot Price" value={marketData.spotPrice.toFixed(2)} unit="€/MWh" change={priceChange} trend={getTrend(priceChange)} icon={Zap} region={ZONE_MAP[activeRegion]} timestamp={marketData.timestamp} isRealData={isRealData} data-id="element-2701" />
        <MetricCard title="Total Load" value={(marketData.load / 1000).toFixed(1)} unit="GW" change={loadChange} trend={getTrend(loadChange)} icon={Activity} region={ZONE_MAP[activeRegion]} timestamp={marketData.timestamp} isRealData={isRealData} data-id="element-2702" />
        <MetricCard title="Wind Generation" value={(marketData.generation.wind / 1000).toFixed(1)} unit="GW" change={windChange} trend={getTrend(windChange)} icon={Wind} region={ZONE_MAP[activeRegion]} timestamp={marketData.timestamp} isRealData={isRealData} data-id="element-2703" />
        <MetricCard title="Solar Generation" value={(marketData.generation.solar / 1000).toFixed(1)} unit="GW" change={solarChange} trend={getTrend(solarChange)} icon={Sun} region={ZONE_MAP[activeRegion]} timestamp={marketData.timestamp} isRealData={isRealData} data-id="element-2704" />
      </div>

      <div key={`charts-${activeRegion}`} className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-2705">
        <div className="bg-white/60 backdrop-blur-xl border border-slate-50 rounded-xl p-6 shadow-sm shadow-slate-200/50" data-id="element-2706">
          <div className="flex items-center justify-between mb-4" data-id="element-2707">
            <h3 className="text-lg font-semibold text-slate-900" data-id="element-2708">
              Generation Mix
            </h3>
            <div className="flex items-center px-2.5 py-0.5 rounded-md bg-slate-50 border border-slate-200" data-id="element-2709">
              <span className="text-[10px] font-semibold text-slate-600 leading-none" data-id="element-2710">
                {ZONE_MAP[activeRegion]}
              </span>
            </div>
          </div>
          <div className="space-y-3" data-id="element-2711">
            {Object.entries(marketData.generation).map(([source, value]: [string, any], idx) => {
            const total = Object.values(marketData.generation).reduce((sum: number, val: any) => sum + val, 0) as number;
            const percentage = value / total * 100;
            const colors: Record<string, string> = {
              wind: 'bg-cyan-500',
              solar: 'bg-yellow-500',
              nuclear: 'bg-purple-500',
              gas: 'bg-orange-500',
              coal: 'bg-slate-700',
              hydro: 'bg-blue-500',
              biomass: 'bg-green-500',
              other: 'bg-slate-400'
            };
            return <div key={source} data-id="element-2712">
                    <div className="flex items-center justify-between text-sm mb-1" data-id="element-2713">
                      <span className="text-slate-700 capitalize" data-id="element-2714">
                        {source}
                      </span>
                      <span className="text-slate-900 font-mono font-medium" data-id="element-2715">
                        {percentage.toFixed(1)}%
                      </span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden" data-id="element-2716">
                      <motion.div key={`${source}-${activeRegion}`} initial={{
                  width: 0
                }} animate={{
                  width: `${percentage}%`
                }} transition={{
                  duration: 1,
                  delay: idx * 0.1
                }} className={`h-full ${colors[source]}`} data-id="element-2717" />
                    </div>
                  </div>;
          })}
          </div>
          <div className="mt-6 pt-6 border-t border-slate-200" data-id="element-2718">
            <div className="flex items-center justify-between" data-id="element-2719">
              <span className="text-sm font-medium text-slate-700" data-id="element-2720">
                Renewable Share
              </span>
              <motion.span key={`renewable-${activeRegion}`} initial={{
              opacity: 0,
              scale: 0.8
            }} animate={{
              opacity: 1,
              scale: 1
            }} className="text-2xl font-bold text-green-600" data-id="element-2721">
                {((marketData.generation.wind + marketData.generation.solar + marketData.generation.hydro) / Object.values(marketData.generation).reduce((sum: number, val: any) => sum + val, 0) * 100).toFixed(1)}
                %
              </motion.span>
            </div>
          </div>
        </div>

        {/* Key Insights with Generate Dashboard Button */}
        {savedKeyPoints.length > 0 ? <div className="bg-white/60 backdrop-blur-xl border border-slate-50 rounded-xl p-6 shadow-sm shadow-slate-200/50 flex flex-col" data-id="element-2722">
            <div className="flex items-center justify-between mb-4" data-id="element-2723">
              <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2" data-id="element-2724">
                <Sparkles className="text-pink-600" size={20} data-id="element-2725" />
                Key Insights
                <span className="px-2 py-0.5 bg-pink-100 text-pink-700 text-xs font-bold rounded-full" data-id="element-2726">
                  {savedKeyPoints.length}
                </span>
              </h3>
              <div className="flex items-center gap-2" data-id="element-2727">
                {savedKeyPoints.length > 0 && <>
                    <button onClick={handleClearInsights} className="px-3 py-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-50 text-xs font-medium rounded-lg transition-all" data-id="element-2728">
                      Clear
                    </button>
                    <button onClick={() => handleOpenDashboardModal()} className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold rounded-lg transition-all shadow-sm" data-id="element-2729">
                      <LayoutDashboard size={14} data-id="element-2730" />
                      Generate Dashboard
                    </button>
                  </>}
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 mb-4 border-b border-slate-200" data-id="element-2731">
              <button onClick={() => setActiveTab('insights')} className={`px-4 py-2 text-sm font-medium transition-all relative ${activeTab === 'insights' ? 'text-pink-600' : 'text-slate-600 hover:text-slate-900'}`} data-id="element-2732">
                Insights
                {activeTab === 'insights' && <motion.div layoutId="activeTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-pink-600" data-id="element-2733" />}
                <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] bg-pink-100 text-pink-700" data-id="element-2734">
                  {insightPoints.length}
                </span>
              </button>
              <button onClick={() => setActiveTab('trades')} className={`px-4 py-2 text-sm font-medium transition-all relative ${activeTab === 'trades' ? 'text-emerald-600' : 'text-slate-600 hover:text-slate-900'}`} data-id="element-2735">
                Trade Ideas
                {activeTab === 'trades' && <motion.div layoutId="activeTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-600" data-id="element-2736" />}
                <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] bg-emerald-100 text-emerald-700" data-id="element-2737">
                  {tradePoints.length}
                </span>
              </button>
            </div>

            {/* Key Points List - Now scrollable to fill card */}
            <div className="space-y-3 flex-1 overflow-y-auto custom-scrollbar pr-2" data-id="element-2738">
              {displayedPoints.length === 0 ? <div className="text-center py-8" data-id="element-2739">
                  <p className="text-sm text-slate-500" data-id="element-2740">
                    No {activeTab === 'insights' ? 'insights' : 'trade ideas'}{' '}
                    yet
                  </p>
                </div> : displayedPoints.map(kp => <div key={kp.id} className="relative group" data-id="element-2741">
                    <button onClick={() => {
              if (showDashboardModal) {
                toggleInsightSelection(kp.id);
              } else {
                handleOpenKeyPointModal(kp);
              }
            }} className={`w-full text-left p-3 bg-white border rounded-lg transition-all ${selectedInsights.has(kp.id) ? 'border-blue-500 bg-blue-50 shadow-md' : 'border-slate-200 hover:border-pink-200 hover:shadow-sm'}`} data-id="element-2742">
                      <div className="flex items-start gap-2 mb-2" data-id="element-2743">
                        {/* Selection checkbox (only visible in modal mode) */}
                        {showDashboardModal && <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${selectedInsights.has(kp.id) ? 'border-blue-500 bg-blue-500' : 'border-slate-300'}`} data-id="element-2744">
                            {selectedInsights.has(kp.id) && <Check size={12} className="text-white" data-id="element-2745" />}
                          </div>}

                        <div className={`p-1.5 rounded-lg flex-shrink-0 ${kp.type === 'trade-idea' ? 'bg-emerald-50' : 'bg-pink-50'}`} data-id="element-2746">
                          {kp.type === 'trade-idea' ? <ArrowUpDown size={12} className="text-emerald-600" data-id="element-2747" /> : <Lightbulb size={12} className="text-pink-600" data-id="element-2748" />}
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-2749">
                          <p className="text-xs text-slate-700 leading-relaxed line-clamp-2" data-id="element-2750">
                            {stripMarkdown(kp.content)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between" data-id="element-2751">
                        <p className="text-[10px] text-slate-400" data-id="element-2752">
                          {formatTimeAgo(kp.timestamp)}
                        </p>
                        <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${kp.type === 'trade-idea' ? 'bg-emerald-100 text-emerald-700' : 'bg-pink-100 text-pink-700'}`} data-id="element-2753">
                          {kp.type === 'trade-idea' ? 'Trade' : 'Insight'}
                        </span>
                      </div>
                    </button>

                    {/* 3-dot menu - hide in modal mode */}
                    {!showDashboardModal && <div className="absolute top-2 right-2 z-20" onClick={e => e.stopPropagation()} data-id="element-2754">
                        <button onClick={e => {
                e.stopPropagation();
                setOpenDropdownId(openDropdownId === kp.id ? null : kp.id);
              }} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors opacity-0 group-hover:opacity-100" data-id="element-2755">
                          <MoreVertical size={14} className="text-slate-400" data-id="element-2756" />
                        </button>

                        <AnimatePresence data-id="element-2757">
                          {openDropdownId === kp.id && <motion.div initial={{
                  opacity: 0,
                  scale: 0.95,
                  y: -10
                }} animate={{
                  opacity: 1,
                  scale: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  scale: 0.95,
                  y: -10
                }} className="absolute right-0 top-full mt-1 w-40 bg-white border border-slate-200 rounded-lg shadow-xl py-1 z-30" data-id="element-2758">
                              <button onClick={e => {
                    e.stopPropagation();
                    handleShareKeyPoint(kp);
                    setOpenDropdownId(null);
                  }} className="w-full px-3 py-2 text-left text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2" data-id="element-2759">
                                <Share2 size={12} data-id="element-2760" />
                                Share
                              </button>
                              <button onClick={e => {
                    e.stopPropagation();
                    navigator.clipboard.writeText(stripMarkdown(kp.fullContent));
                    setOpenDropdownId(null);
                  }} className="w-full px-3 py-2 text-left text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2" data-id="element-2761">
                                <Copy size={12} data-id="element-2762" />
                                Copy text
                              </button>
                              <button onClick={e => {
                    e.stopPropagation();
                    handleDeleteKeyPoint(kp.id, e);
                    setOpenDropdownId(null);
                  }} className="w-full px-3 py-2 text-left text-xs text-red-600 hover:bg-red-50 flex items-center gap-2" data-id="element-2763">
                                <Trash2 size={12} data-id="element-2764" />
                                Delete
                              </button>
                            </motion.div>}
                        </AnimatePresence>
                      </div>}
                  </div>)}
            </div>
          </div> : <div className="bg-white/60 backdrop-blur-xl border border-slate-50 rounded-xl p-6 shadow-sm shadow-slate-200/50" data-id="element-2765">
            <h3 className="text-lg font-semibold text-slate-900 mb-4" data-id="element-2766">
              Model Performance
            </h3>
            <div className="space-y-4" data-id="element-2767">
              {generateModelAccuracy().map((model, idx) => <div key={model.model} data-id="element-2768">
                  <div className="flex items-center justify-between mb-2" data-id="element-2769">
                    <span className="text-sm font-medium text-slate-700" data-id="element-2770">
                      {model.model}
                    </span>
                    <span className="text-sm font-bold text-slate-900" data-id="element-2771">
                      {model.score}/100
                    </span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden" data-id="element-2772">
                    <motion.div initial={{
                width: 0
              }} animate={{
                width: `${model.score}%`
              }} transition={{
                duration: 1,
                delay: idx * 0.1
              }} className={`h-full ${model.model === 'EPT-2' ? 'bg-gradient-to-r from-pink-500 to-purple-500' : 'bg-slate-400'}`} data-id="element-2773" />
                  </div>
                </div>)}
            </div>
            <div className="mt-6 pt-6 border-t border-slate-200" data-id="element-2774">
              <p className="text-xs text-slate-600" data-id="element-2775">
                EPT-2 maintains highest accuracy across all forecast horizons
              </p>
            </div>
          </div>}
      </div>

      {/* Generate Dashboard Modal */}
      <AnimatePresence data-id="element-2776">
        {showDashboardModal && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[100]" onClick={() => setShowDashboardModal(false)} data-id="element-2777" />
            <div className="fixed inset-0 z-[101] flex items-center justify-center p-4 pointer-events-none" data-id="element-2778">
              <motion.div initial={{
            opacity: 0,
            scale: 0.95,
            y: 20
          }} animate={{
            opacity: 1,
            scale: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.95,
            y: 20
          }} className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl pointer-events-auto overflow-hidden max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()} data-id="element-2779">
                {/* Header */}
                <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-blue-50 to-indigo-50" data-id="element-2780">
                  <div className="flex items-start justify-between" data-id="element-2781">
                    <div className="flex items-center gap-3" data-id="element-2782">
                      <div className="p-2.5 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500" data-id="element-2783">
                        <LayoutDashboard size={20} className="text-white" data-id="element-2784" />
                      </div>
                      <div data-id="element-2785">
                        <h3 className="text-lg font-bold text-slate-900" data-id="element-2786">
                          Load Dashboard with Insights
                        </h3>
                        <p className="text-sm text-slate-600" data-id="element-2787">
                          Select a dashboard to load with your{' '}
                          {selectedInsights.size} selected insights
                        </p>
                      </div>
                    </div>
                    <button onClick={() => setShowDashboardModal(false)} className="p-1.5 hover:bg-white/50 rounded-lg transition-colors" data-id="element-2788">
                      <X size={20} className="text-slate-400" data-id="element-2789" />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6" data-id="element-2790">
                  {/* AI Analysis Summary */}
                  {(() => {
                const analysis = analyzeInsights();
                const suggestedDashboard = availableDashboards.find(d => d.id === analysis.matchedDashboard);
                return <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-4 border border-purple-200" data-id="element-2791">
                        <div className="flex items-start gap-3" data-id="element-2792">
                          <div className="p-2 rounded-lg bg-purple-100" data-id="element-2793">
                            <Sparkles size={16} className="text-purple-600" data-id="element-2794" />
                          </div>
                          <div className="flex-1" data-id="element-2795">
                            <h4 className="text-sm font-semibold text-slate-900 mb-2" data-id="element-2796">
                              AI Analysis
                            </h4>
                            <div className="space-y-2 text-xs text-slate-700" data-id="element-2797">
                              <p data-id="element-2798">
                                Detected{' '}
                                <strong data-id="element-2799">{analysis.regions.join(', ')}</strong>{' '}
                                with focus on{' '}
                                <strong data-id="element-2800">{analysis.variables.join(', ')}</strong>
                              </p>
                              {suggestedDashboard && analysis.matchScore > 0 && <div className="mt-3 p-3 bg-white rounded-lg border border-purple-200" data-id="element-2801">
                                    <p className="text-purple-900 font-medium mb-1" data-id="element-2802">
                                      💡 Recommended Dashboard
                                    </p>
                                    <p className="text-slate-700" data-id="element-2803">
                                      <strong data-id="element-2804">{suggestedDashboard.name}</strong>{' '}
                                      - Best match based on your insights
                                    </p>
                                    {!selectedDashboard && <button onClick={() => setSelectedDashboard(suggestedDashboard.id)} className="mt-2 text-xs font-medium text-purple-600 hover:text-purple-700 underline" data-id="element-2805">
                                        Select this dashboard
                                      </button>}
                                  </div>}
                            </div>
                          </div>
                        </div>
                      </div>;
              })()}

                  {/* Selected Insights Summary */}
                  {selectedInsights.size > 0 && <div className="bg-gradient-to-r from-pink-50 to-purple-50 rounded-xl p-4 border border-pink-200" data-id="element-2806">
                      <div className="flex items-start gap-3" data-id="element-2807">
                        <div className="p-2 rounded-lg bg-pink-100" data-id="element-2808">
                          <Sparkles size={16} className="text-pink-600" data-id="element-2809" />
                        </div>
                        <div className="flex-1" data-id="element-2810">
                          <h4 className="text-sm font-semibold text-slate-900 mb-2" data-id="element-2811">
                            Selected Insights ({selectedInsights.size})
                          </h4>
                          <div className="flex flex-wrap gap-2" data-id="element-2812">
                            {Array.from(selectedInsights).slice(0, 3).map(id => {
                        const insight = savedKeyPoints.find(kp => kp.id === id);
                        if (!insight) return null;
                        return <div key={id} className="px-2.5 py-1 bg-white rounded-lg border border-pink-200 text-xs text-slate-700 line-clamp-1 max-w-[200px]" data-id="element-2813">
                                    {stripMarkdown(insight.content).slice(0, 40)}
                                    ...
                                  </div>;
                      })}
                            {selectedInsights.size > 3 && <div className="px-2.5 py-1 bg-pink-100 rounded-lg text-xs font-medium text-pink-700" data-id="element-2814">
                                +{selectedInsights.size - 3} more
                              </div>}
                          </div>
                        </div>
                      </div>
                    </div>}

                  {/* Dashboard Selection */}
                  <div data-id="element-2815">
                    <h4 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2" data-id="element-2816">
                      <LayoutDashboard size={16} className="text-blue-600" data-id="element-2817" />
                      Select Dashboard to Load
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2818">
                      {availableDashboards.map(dash => <button key={dash.id} onClick={() => setSelectedDashboard(dash.id)} className={`group relative p-5 rounded-xl border-2 transition-all text-left ${selectedDashboard === dash.id ? 'border-blue-500 bg-blue-50 shadow-lg shadow-blue-500/20' : 'border-slate-200 hover:border-blue-300 hover:shadow-md'}`} data-id="element-2819">
                          {/* Selection indicator */}
                          <div className={`absolute top-4 right-4 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${selectedDashboard === dash.id ? 'border-blue-500 bg-blue-500' : 'border-slate-300 group-hover:border-blue-400'}`} data-id="element-2820">
                            {selectedDashboard === dash.id && <Check size={12} className="text-white" data-id="element-2821" />}
                          </div>

                          {/* Dashboard icon */}
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 transition-all ${selectedDashboard === dash.id ? 'bg-blue-100' : 'bg-slate-100 group-hover:bg-blue-50'}`} data-id="element-2822">
                            <dash.icon size={24} className={selectedDashboard === dash.id ? 'text-blue-600' : 'text-slate-600 group-hover:text-blue-600'} data-id="element-2823" />
                          </div>

                          {/* Dashboard info */}
                          <h5 className="font-semibold text-slate-900 mb-1" data-id="element-2824">
                            {dash.name}
                          </h5>
                          <p className="text-xs text-slate-600 leading-relaxed" data-id="element-2825">
                            {dash.description}
                          </p>

                          {/* Preview badge */}
                          <div className="mt-3 flex items-center gap-2" data-id="element-2826">
                            <span className="text-[10px] font-medium text-slate-500" data-id="element-2827">
                              6 cards • Live data
                            </span>
                          </div>
                        </button>)}
                    </div>
                  </div>

                  {/* Info Box */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-2828">
                    <div className="flex gap-3" data-id="element-2829">
                      <Info className="text-blue-600 flex-shrink-0 mt-0.5" size={16} data-id="element-2830" />
                      <div className="text-sm text-blue-900" data-id="element-2831">
                        <p className="font-medium mb-1" data-id="element-2832">How it works</p>
                        <p className="text-blue-700 text-xs leading-relaxed" data-id="element-2833">
                          Your selected insights will be highlighted in the
                          dashboard. The dashboard will load with relevant data
                          and charts based on the regions, timeframes, and
                          variables mentioned in your insights.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-slate-200 bg-slate-50 flex justify-between items-center" data-id="element-2834">
                  <button onClick={() => setShowDashboardModal(false)} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors" data-id="element-2835">
                    Cancel
                  </button>
                  <button onClick={handleGenerateDashboard} disabled={isGenerating || !selectedDashboard} className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white text-sm font-semibold rounded-xl transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2" data-id="element-2836">
                    {isGenerating ? <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-2837" />
                        {generationStep || 'Loading Dashboard...'}
                      </> : <>
                        <LayoutDashboard size={16} data-id="element-2838" />
                        Load Dashboard
                      </>}
                  </button>
                </div>
              </motion.div>
            </div>
          </>}
      </AnimatePresence>

      <div key={`regional-${activeRegion}`} className="bg-white/60 backdrop-blur-xl border border-slate-50 rounded-xl p-6 shadow-sm shadow-slate-200/50" data-id="element-2839">
        <h3 className="text-lg font-semibold text-slate-900 mb-4" data-id="element-2840">
          Regional Market Summary
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-2841">
          {(['DE', 'ES', 'UK', 'FR'] as MarketZone[]).map(zone => {
          const data = regionsDataMap[zone];
          if (!data) return null;
          const isActive = zone === ZONE_MAP[activeRegion];
          const regionName = Object.keys(ZONE_MAP).find(key => ZONE_MAP[key] === zone) || zone;
          return <RegionalSummaryCard key={zone} zone={zone} data={data} isActive={isActive} onSelect={() => setActiveRegion(regionName)} data-id="element-2842" />;
        })}
        </div>
      </div>

      {/* Key Point Detail Modal */}
      <AnimatePresence data-id="element-2843">
        {selectedKeyPoint && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm z-50" onClick={() => setSelectedKeyPoint(null)} data-id="element-2844" />
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none" data-id="element-2845">
              <motion.div initial={{
            opacity: 0,
            scale: 0.95,
            y: 20
          }} animate={{
            opacity: 1,
            scale: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.95,
            y: 20
          }} className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl pointer-events-auto overflow-hidden max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()} data-id="element-2846">
                <div className={`p-6 border-b border-slate-200 ${selectedKeyPoint.type === 'trade-idea' ? 'bg-gradient-to-r from-emerald-50 to-green-50' : 'bg-gradient-to-r from-pink-50 to-purple-50'}`} data-id="element-2847">
                  <div className="flex items-start justify-between" data-id="element-2848">
                    <div className="flex items-center gap-3" data-id="element-2849">
                      <div className={`p-2.5 rounded-xl ${selectedKeyPoint.type === 'trade-idea' ? 'bg-emerald-100' : 'bg-pink-100'}`} data-id="element-2850">
                        {selectedKeyPoint.type === 'trade-idea' ? <ArrowUpDown size={24} className="text-emerald-600" data-id="element-2851" /> : <Bookmark size={24} className="text-pink-600" data-id="element-2852" />}
                      </div>
                      <div data-id="element-2853">
                        <h3 className="text-lg font-bold text-slate-900" data-id="element-2854">
                          {selectedKeyPoint.type === 'trade-idea' ? 'Trade Idea' : 'Saved Insight'}
                        </h3>
                        <p className="text-sm text-slate-600" data-id="element-2855">
                          Saved {formatTimeAgo(selectedKeyPoint.timestamp)}
                        </p>
                      </div>
                    </div>
                    <button onClick={() => setSelectedKeyPoint(null)} className="p-1.5 hover:bg-white/50 rounded-lg transition-colors" data-id="element-2856">
                      <X size={20} className="text-slate-400" data-id="element-2857" />
                    </button>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-6 custom-scrollbar" data-id="element-2858">
                  <div className="prose prose-sm max-w-none" data-id="element-2859">
                    <div className="text-sm text-slate-700 leading-relaxed space-y-3" data-id="element-2860">
                      {renderMarkdown(selectedKeyPoint.fullContent)}
                    </div>
                  </div>
                </div>

                <div className="p-6 border-t border-slate-200 bg-slate-50 flex justify-between items-center" data-id="element-2861">
                  <button onClick={() => {
                handleDeleteKeyPoint(selectedKeyPoint.id);
                setSelectedKeyPoint(null);
              }} className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors" data-id="element-2862">
                    Delete
                  </button>
                  <button onClick={() => setSelectedKeyPoint(null)} className="px-6 py-2 bg-slate-900 text-white text-sm font-semibold rounded-xl hover:bg-slate-800 transition-all" data-id="element-2863">
                    Close
                  </button>
                </div>
              </motion.div>
            </div>
          </>}
      </AnimatePresence>

      {/* Forecast Chart/Map Modal */}
      <AnimatePresence data-id="element-2864">
        {selectedForecast && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50" onClick={() => setSelectedForecast(null)} data-id="element-2865" />
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none" data-id="element-2866">
              <motion.div initial={{
            opacity: 0,
            scale: 0.95,
            y: 20
          }} animate={{
            opacity: 1,
            scale: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.95,
            y: 20
          }} className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl pointer-events-auto overflow-hidden max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()} data-id="element-2867">
                {/* Header */}
                <div className={`p-6 border-b border-slate-200 ${selectedForecast.type === 'wind' ? 'bg-gradient-to-r from-cyan-50 to-blue-50' : selectedForecast.type === 'solar' ? 'bg-gradient-to-r from-orange-50 to-yellow-50' : selectedForecast.type === 'temperature' ? 'bg-gradient-to-r from-purple-50 to-pink-50' : selectedForecast.type === 'price' ? 'bg-gradient-to-r from-blue-50 to-indigo-50' : 'bg-gradient-to-r from-emerald-50 to-green-50'}`} data-id="element-2868">
                  <div className="flex items-start justify-between" data-id="element-2869">
                    <div className="flex items-center gap-3" data-id="element-2870">
                      <div className={`p-2.5 rounded-xl ${selectedForecast.type === 'wind' ? 'bg-cyan-100' : selectedForecast.type === 'solar' ? 'bg-orange-100' : selectedForecast.type === 'temperature' ? 'bg-purple-100' : selectedForecast.type === 'price' ? 'bg-blue-100' : 'bg-emerald-100'}`} data-id="element-2871">
                        {selectedForecast.type === 'wind' && <Wind size={24} className="text-cyan-600" data-id="element-2872" />}
                        {selectedForecast.type === 'solar' && <Sun size={24} className="text-orange-600" data-id="element-2873" />}
                        {selectedForecast.type === 'temperature' && <Activity size={24} className="text-purple-600" data-id="element-2874" />}
                        {selectedForecast.type === 'price' && <Zap size={24} className="text-blue-600" data-id="element-2875" />}
                      </div>
                      <div data-id="element-2876">
                        <h3 className="text-lg font-bold text-slate-900 capitalize" data-id="element-2877">
                          {selectedForecast.type} Forecast
                        </h3>
                        <p className="text-sm text-slate-600" data-id="element-2878">
                          {selectedForecast.region} • Next 7 days
                        </p>
                      </div>
                    </div>
                    <button onClick={() => setSelectedForecast(null)} className="p-1.5 hover:bg-white/50 rounded-lg transition-colors" data-id="element-2879">
                      <X size={20} className="text-slate-400" data-id="element-2880" />
                    </button>
                  </div>
                </div>

                {/* Chart Content */}
                <div className="flex-1 overflow-y-auto p-6 custom-scrollbar h-[500px]" data-id="element-2881">
                  <div className="space-y-6" data-id="element-2882">
                    {/* Real Chart from ChartsPanel */}
                    <div className="h-[400px]" data-id="element-2883">
                      <ChartsPanel activeRegion={selectedForecast.region} activeVariable={getVariableType(selectedForecast.type)} dashboardContext={{
                    defaultTitle: selectedForecast.title
                  }} data-id="element-2884" />
                    </div>

                    {/* Data Table */}
                    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-id="element-2885">
                      <table className="w-full text-sm" data-id="element-2886">
                        <thead className="bg-slate-50 border-b border-slate-200" data-id="element-2887">
                          <tr data-id="element-2888">
                            <th className="px-4 py-3 text-left font-semibold text-slate-700" data-id="element-2889">
                              Day
                            </th>
                            <th className="px-4 py-3 text-left font-semibold text-slate-700" data-id="element-2890">
                              Min
                            </th>
                            <th className="px-4 py-3 text-left font-semibold text-slate-700" data-id="element-2891">
                              Max
                            </th>
                            <th className="px-4 py-3 text-left font-semibold text-slate-700" data-id="element-2892">
                              Avg
                            </th>
                            <th className="px-4 py-3 text-left font-semibold text-slate-700" data-id="element-2893">
                              Confidence
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100" data-id="element-2894">
                          {Array.from({
                        length: 7
                      }).map((_, i) => {
                        const min = Math.random() * 30 + 20;
                        const max = min + Math.random() * 40 + 20;
                        const avg = (min + max) / 2;
                        const confidence = Math.random() * 20 + 75;
                        const unit = selectedForecast.type === 'wind' ? 'm/s' : selectedForecast.type === 'solar' ? 'W/m²' : selectedForecast.type === 'temperature' ? '°C' : '€/MWh';
                        return <tr key={i} className="hover:bg-slate-50" data-id="element-2895">
                                <td className="px-4 py-3 font-medium text-slate-900" data-id="element-2896">
                                  Day {i + 1}
                                </td>
                                <td className="px-4 py-3 text-slate-600" data-id="element-2897">
                                  {min.toFixed(1)} {unit}
                                </td>
                                <td className="px-4 py-3 text-slate-600" data-id="element-2898">
                                  {max.toFixed(1)} {unit}
                                </td>
                                <td className="px-4 py-3 font-medium text-slate-900" data-id="element-2899">
                                  {avg.toFixed(1)} {unit}
                                </td>
                                <td className="px-4 py-3" data-id="element-2900">
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${confidence > 85 ? 'bg-green-100 text-green-700' : confidence > 75 ? 'bg-yellow-100 text-yellow-700' : 'bg-orange-100 text-orange-700'}`} data-id="element-2901">
                                    {confidence.toFixed(0)}%
                                  </span>
                                </td>
                              </tr>;
                      })}
                        </tbody>
                      </table>
                    </div>

                    {/* Forecast Notes */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-2902">
                      <div className="flex gap-3" data-id="element-2903">
                        <Info className="text-blue-600 flex-shrink-0 mt-0.5" size={16} data-id="element-2904" />
                        <div className="text-sm text-blue-900" data-id="element-2905">
                          <p className="font-medium mb-1" data-id="element-2906">Forecast Notes</p>
                          <p className="text-blue-700" data-id="element-2907">
                            Real-time forecast data from EPT-2 proprietary
                            model, ECMWF, and GFS. The chart above shows actual
                            forecast data with model agreement and uncertainty
                            bands.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-slate-200 bg-slate-50 flex justify-between items-center" data-id="element-2908">
                  <div className="text-xs text-slate-500" data-id="element-2909">
                    Data source: EPT-2 Model • Updated 15 min ago
                  </div>
                  <button onClick={() => setSelectedForecast(null)} className="px-6 py-2 bg-slate-900 text-white text-sm font-semibold rounded-xl hover:bg-slate-800 transition-all" data-id="element-2910">
                    Close
                  </button>
                </div>
              </motion.div>
            </div>
          </>}
      </AnimatePresence>

      <div key={`footer-${activeRegion}`} className="bg-slate-50/50 border border-slate-100 rounded-lg p-4" data-id="element-2911"></div>
    </div>;
}